/* Native details remain usable without JavaScript. Groups never cross sections. */
(() => {
  const selector = 'details.facets__group, details.ks-product__accordion, details.ks-collapsible-tabs__item';
  const states = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const group = (el) => el.closest('.facets__form, .ks-product__info-column, .ks-collapsible-tabs__list');
  const peers = (el) => [...(group(el)?.querySelectorAll(selector) || [])].filter(other => group(other) === group(el));

  function setOpen(el, open, instant = false) {
    const state = states.get(el);
    if (!state) return;
    const start = el.getBoundingClientRect().height;
    state.animation?.cancel();
    state.animation = null;
    state.target = open;
    state.summary.setAttribute('aria-expanded', String(open));
    state.body.inert = !open;
    el.style.height = '';
    el.open = open;
    const end = el.getBoundingClientRect().height;
    if (instant || reduced.matches || !el.animate || Math.abs(start - end) < 1) {
      el.style.overflow = '';
      return;
    }
    // Keep the body rendered until the closing animation completes.
    el.open = true;
    el.style.overflow = 'hidden';
    const css = getComputedStyle(el);
    const edges = css.boxSizing === 'border-box' ? 0 :
      parseFloat(css.paddingTop) + parseFloat(css.paddingBottom) + parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
    const animation = el.animate([
      { height: `${Math.max(0, start - edges)}px` },
      { height: `${Math.max(0, end - edges)}px` }
    ], { duration: 260, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' });
    state.animation = animation;
    animation.onfinish = () => {
      if (state.animation !== animation) return;
      el.open = state.target;
      animation.cancel();
      state.animation = null;
      el.style.overflow = '';
    };
  }

  function initialize(scope) {
    const items = [...(scope.querySelectorAll?.(selector) || [])];
    if (scope.matches?.(selector)) items.unshift(scope);
    items.forEach(el => {
      if (states.has(el) || !group(el)) return;
      const summary = el.querySelector(':scope > summary');
      const body = summary?.nextElementSibling;
      if (!summary || !body) return;
      states.set(el, { summary, body, target: el.open, animation: null });
      summary.setAttribute('aria-expanded', String(el.open));
      body.inert = !el.open;
      summary.addEventListener('click', event => {
        if (event.target.closest('a, button, input, select, textarea')) return;
        event.preventDefault();
        const open = !states.get(el).target;
        if (open) peers(el).forEach(other => {
          if (other !== el && states.get(other)?.target) setOpen(other, false);
        });
        setOpen(el, open);
      });
      // Resize only an animation in progress; settled panels use natural height.
      const resize = new ResizeObserver(() => {
        if (states.get(el).animation) setOpen(el, states.get(el).target);
      });
      resize.observe(body);
      states.get(el).resize = resize;
      el.addEventListener('toggle', () => {
        const state = states.get(el);
        if (state.animation || state.target === el.open) return;
        if (el.open) peers(el).forEach(other => {
          if (other !== el && states.get(other)?.target) setOpen(other, false);
        });
        setOpen(el, el.open, true);
      });
    });
    const groups = new Set(items.map(group).filter(Boolean));
    groups.forEach(root => {
      let found = false;
      root.querySelectorAll(selector).forEach(el => {
        if (group(el) !== root || !states.get(el)?.target) return;
        if (found) setOpen(el, false, true);
        found = true;
      });
    });
  }
  initialize(document);
  new MutationObserver(records => {
    records.forEach(record => {
      record.removedNodes.forEach(node => {
        if (!(node instanceof Element) || node.isConnected) return;
        [node, ...node.querySelectorAll(selector)].forEach(el => {
          const state = states.get(el);
          state?.animation?.cancel();
          state?.resize?.disconnect();
          if (state) states.delete(el);
        });
      });
      record.addedNodes.forEach(node => {
        if (node instanceof Element) initialize(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
